'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import { Card, CardBody, CardHeader, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Button, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Chip, Progress, Divider, useDisclosure, Pagination, Popover, PopoverTrigger, PopoverContent, RadioGroup, Radio, CheckboxGroup, Checkbox } from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { cn } from '@heroui/theme';
import type { Address } from 'viem';
import type { ChainType } from '@/constants/chain-specific';
import { ChainId, chainDefaults } from '@/constants/chain-specific';
import { useLiquidityPositionsContext, type DexLoadingState } from '@/context';
import type { LiquidityPosition } from '@/hooks/liquidity/useLiquidityPositions';
import { TokenLogo } from '@/components/Tokens/TokenLogo';
import { SendLPModal } from '@/components/Liquidity/SendLPModal';
import { CrossChainPositionsDropdown } from '@/components/Liquidity/CrossChainPositionsDropdown';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import { useIsMobile } from '@/hooks/utility/useIsMobile';
import { formatNumber } from '@/util/balance/formatNumber';
import { useLiquidityPositionsPriceEnrichment } from '@/hooks/liquidity/useLiquidityPositionsPriceEnrichment';
interface ProgressivePositionsListProps {
  chainId?: ChainType;
  className?: string;
  tokenData?: Record<Address, any>;
  // Navigation callbacks
  onNavigateToAdd?: (position?: LiquidityPosition) => void;
  onNavigateToRemove?: (position: LiquidityPosition) => void;
}
type SortKey = 'value' | 'apr' | 'created' | 'pool';
type FilterType = 'all' | 'active' | 'inactive' | 'v2' | 'v3';

// Pagination settings
const ITEMS_PER_PAGE = 15;
export function ProgressivePositionsList(t0) {
  const $ = _c(103);
  const {
    chainId,
    className,
    tokenData,
    onNavigateToAdd,
    onNavigateToRemove
  } = t0;
  const {
    positions: rawPositions,
    dexStates,
    isLoading,
    isComplete,
    error,
    refetch
  } = useLiquidityPositionsContext();
  const {
    positions: enrichedPositions,
    isLoading: pricesLoading
  } = useLiquidityPositionsPriceEnrichment(chainId);
  const positions = enrichedPositions.length > 0 ? enrichedPositions : rawPositions;
  console.log("[ProgressivePositionsList] Component rendered", {
    positionCount: positions.length,
    isLoading,
    isComplete,
    dexStatesSize: dexStates.size,
    hasError: !!error,
    pricesLoading
  });
  const [sortKey, setSortKey] = useState("value");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedDexes, setSelectedDexes] = useState(t1);
  const [selectedPositionId, setSelectedPositionId] = useState(null);
  const [sendPosition, setSendPosition] = useState(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [crossChainPositions, setCrossChainPositions] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [selectedCrossChainIds, setSelectedCrossChainIds] = useState(t3);
  const {
    isOpen: isSendModalOpen,
    onOpen: onSendModalOpen,
    onOpenChange: onSendModalOpenChange
  } = useDisclosure();
  const isMobile = useIsMobile();
  let t4;
  if ($[3] !== positions) {
    t4 = positions.reduce(_temp, 0);
    $[3] = positions;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const totalValue = t4;
  const t5 = positions.length;
  let t6;
  if ($[5] !== positions) {
    t6 = positions.filter(_temp2);
    $[5] = positions;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== positions) {
    t7 = positions.length > 0 ? positions.reduce(_temp3, 0) / positions.length : 0;
    $[7] = positions;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== positions.length || $[10] !== t6.length || $[11] !== t7 || $[12] !== totalValue) {
    t8 = {
      totalPositions: t5,
      activePositions: t6.length,
      totalValue,
      avgApr: t7
    };
    $[9] = positions.length;
    $[10] = t6.length;
    $[11] = t7;
    $[12] = totalValue;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const stats = t8;
  const dexMap = new Map();
  positions.forEach(p_2 => {
    if (!dexMap.has(p_2.dex)) {
      dexMap.set(p_2.dex, {
        slug: p_2.dex,
        name: p_2.dexName
      });
    }
  });
  let t9;
  if ($[14] !== selectedPositionId) {
    t9 = position => {
      const positionId = `${position.contractAddress}-${position.token0.address}-${position.token1.address}-${position.id}`;
      setSelectedPositionId(selectedPositionId === positionId ? null : positionId);
    };
    $[14] = selectedPositionId;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleRowClick = t9;
  let t10;
  if ($[16] !== onNavigateToRemove) {
    t10 = position_0 => {
      if (onNavigateToRemove) {
        onNavigateToRemove(position_0);
      }
    };
    $[16] = onNavigateToRemove;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleRemoveClick = t10;
  let t11;
  if ($[18] !== onNavigateToAdd) {
    t11 = position_1 => {
      if (onNavigateToAdd) {
        onNavigateToAdd(position_1);
      }
    };
    $[18] = onNavigateToAdd;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const handleIncreaseClick = t11;
  let t12;
  if ($[20] !== onSendModalOpen) {
    t12 = position_2 => {
      setSendPosition(position_2);
      onSendModalOpen();
    };
    $[20] = onSendModalOpen;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const handleSendClick = t12;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (positions_0, chainIds) => {
      setCrossChainPositions(positions_0);
      setSelectedCrossChainIds(chainIds);
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const handleCrossChainSelection = t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => {
      setCrossChainPositions([]);
      setSelectedCrossChainIds([]);
    };
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const clearCrossChainView = t14;
  let allPositions;
  if ($[24] !== crossChainPositions || $[25] !== filter || $[26] !== positions || $[27] !== selectedDexes || $[28] !== sortDirection || $[29] !== sortKey) {
    allPositions = [...positions, ...crossChainPositions];
    if (selectedDexes.length > 0) {
      allPositions = allPositions.filter(p_3 => selectedDexes.includes(p_3.dex));
    }
    bb0: switch (filter) {
      case "active":
        {
          allPositions = allPositions.filter(_temp4);
          break bb0;
        }
      case "inactive":
        {
          allPositions = allPositions.filter(_temp5);
          break bb0;
        }
      case "v2":
        {
          allPositions = allPositions.filter(_temp6);
          break bb0;
        }
      case "v3":
        {
          allPositions = allPositions.filter(_temp7);
        }
    }
    let t15;
    if ($[31] !== sortDirection || $[32] !== sortKey) {
      t15 = (a, b) => {
        let aValue;
        let bValue;
        bb75: switch (sortKey) {
          case "value":
            {
              aValue = a.totalValueUsd;
              bValue = b.totalValueUsd;
              break bb75;
            }
          case "apr":
            {
              aValue = a.apr;
              bValue = b.apr;
              break bb75;
            }
          case "pool":
            {
              aValue = `${a.token0.symbol}/${a.token1.symbol}`;
              bValue = `${b.token0.symbol}/${b.token1.symbol}`;
              break bb75;
            }
          default:
            {
              aValue = 0;
              bValue = 0;
            }
        }
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortDirection === "desc" ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
        }
        return sortDirection === "desc" ? (bValue as number) - (aValue as number) : (aValue as number) - (bValue as number);
      };
      $[31] = sortDirection;
      $[32] = sortKey;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    allPositions.sort(t15);
    $[24] = crossChainPositions;
    $[25] = filter;
    $[26] = positions;
    $[27] = selectedDexes;
    $[28] = sortDirection;
    $[29] = sortKey;
    $[30] = allPositions;
  } else {
    allPositions = $[30];
  }
  const filteredAndSortedPositions = allPositions;
  const totalPages = Math.ceil(filteredAndSortedPositions.length / ITEMS_PER_PAGE);
  const showPagination = filteredAndSortedPositions.length > ITEMS_PER_PAGE;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  let t15;
  if ($[34] !== endIndex || $[35] !== filteredAndSortedPositions || $[36] !== startIndex) {
    t15 = filteredAndSortedPositions.slice(startIndex, endIndex);
    $[34] = endIndex;
    $[35] = filteredAndSortedPositions;
    $[36] = startIndex;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const paginatedPositions = t15;
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = newFilter => {
      setFilter(newFilter);
      setCurrentPage(1);
    };
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const handleFilterChange = t16;
  const activeFilterCount = (filter !== "all" ? 1 : 0) + (selectedDexes.length > 0 ? 1 : 0);
  let t17;
  if ($[39] !== sortDirection || $[40] !== sortKey) {
    t17 = key => {
      if (sortKey === key) {
        setSortDirection(sortDirection === "desc" ? "asc" : "desc");
      } else {
        setSortKey(key);
        setSortDirection("desc");
      }
      setCurrentPage(1);
    };
    $[39] = sortDirection;
    $[40] = sortKey;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const handleSort = t17;
  let t18;
  if ($[42] !== dexStates || $[43] !== isComplete || $[44] !== isLoading) {
    t18 = () => {
      if (!isLoading && isComplete) {
        return null;
      }
      const dexStatesArray = Array.from(dexStates.values());
      if (dexStatesArray.length === 0) {
        return null;
      }
      const currentlyLoadingDex = dexStatesArray.find(_temp8);
      if (!currentlyLoadingDex) {
        return null;
      }
      const percent = currentlyLoadingDex.totalPairs > 0 ? currentlyLoadingDex.checkedPairs / currentlyLoadingDex.totalPairs * 100 : 0;
      const statusText = currentlyLoadingDex.status === "loading-top-pools" ? "Checking top pools..." : currentlyLoadingDex.status === "loading-all-pairs" ? "Scanning all pairs..." : "Loading...";
      const totalFoundPositions = dexStatesArray.reduce(_temp9, 0);
      return <Card className="mb-4"><CardBody className="py-4"><div className="flex flex-col gap-3"><div className="flex items-center justify-between"><div className="flex items-center gap-3">{currentlyLoadingDex.dexLogo && <img src={currentlyLoadingDex.dexLogo} alt={currentlyLoadingDex.dexName} className="w-8 h-8 rounded-full" />}<div className="flex flex-col"><span className="font-semibold">{currentlyLoadingDex.dexName}</span><span className="text-xs text-default-500">{statusText}</span></div></div>{totalFoundPositions > 0 && <Chip size="sm" color="success" variant="flat">{totalFoundPositions} position{totalFoundPositions !== 1 ? "s" : ""} found</Chip>}</div>{currentlyLoadingDex.status === "loading-all-pairs" && currentlyLoadingDex.totalPairs > 0 ? <Progress value={percent} size="sm" radius="full" showValueLabel={true} classNames={{
              base: "w-full",
              track: "bg-default-100",
              indicator: "bg-gradient-to-r from-primary to-secondary",
              label: "text-xs text-default-600",
              value: "text-xs text-default-600"
            }} label={`${currentlyLoadingDex.checkedPairs} / ${currentlyLoadingDex.totalPairs} pairs`} /> : <Progress isIndeterminate={true} size="sm" radius="full" classNames={{
              base: "w-full",
              track: "bg-default-100",
              indicator: "bg-gradient-to-r from-primary to-secondary"
            }} />}</div></CardBody></Card>;
    };
    $[42] = dexStates;
    $[43] = isComplete;
    $[44] = isLoading;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const renderDexLoadingStates = t18;
  if (error) {
    let t19;
    let t20;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <Icon icon="mdi:alert-circle" className="text-6xl text-danger mx-auto mb-4" />;
      t20 = <h3 className="text-xl font-semibold mb-2">Error Loading Positions</h3>;
      $[46] = t19;
      $[47] = t20;
    } else {
      t19 = $[46];
      t20 = $[47];
    }
    let t21;
    if ($[48] !== error.message) {
      t21 = <p className="text-default-500 mb-6">{error.message}</p>;
      $[48] = error.message;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    let t22;
    if ($[50] !== refetch) {
      t22 = <Button color="primary" onPress={refetch}>Try Again</Button>;
      $[50] = refetch;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    let t23;
    if ($[52] !== t21 || $[53] !== t22) {
      t23 = <CardBody className="text-center py-12">{t19}{t20}{t21}{t22}</CardBody>;
      $[52] = t21;
      $[53] = t22;
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    let t24;
    if ($[55] !== className || $[56] !== t23) {
      t24 = <Card className={className}>{t23}</Card>;
      $[55] = className;
      $[56] = t23;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    return t24;
  }
  if (!isLoading && positions.length === 0 && isComplete) {
    let t19;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <CardBody className="text-center py-12"><Icon icon="mdi:liquidity" className="text-6xl text-default-300 mx-auto mb-4" /><h3 className="text-xl font-semibold mb-2">No Liquidity Positions</h3><p className="text-default-500 mb-6">You haven't provided liquidity to any pools yet. Start by adding liquidity to earn fees.</p></CardBody>;
      $[58] = t19;
    } else {
      t19 = $[58];
    }
    let t20;
    if ($[59] !== className) {
      t20 = <Card className={className}>{t19}</Card>;
      $[59] = className;
      $[60] = t20;
    } else {
      t20 = $[60];
    }
    return t20;
  }
  let t19;
  if ($[61] !== renderDexLoadingStates) {
    t19 = renderDexLoadingStates();
    $[61] = renderDexLoadingStates;
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== isLoading || $[64] !== isMobile || $[65] !== positions.length || $[66] !== refetch || $[67] !== stats) {
    t20 = positions.length > 0 && <Card className="mb-4"><CardHeader><div className="flex items-center justify-between w-full"><h2 className="text-xl font-semibold">Overview</h2><Button variant="light" size="lg" isIconOnly={true} onPress={refetch} isLoading={isLoading}><Icon icon="mdi:refresh" width="20px" /></Button></div></CardHeader><CardBody><div className="grid grid-cols-2 md:grid-cols-3 gap-4">{!isMobile && <div className="text-center"><div className="text-2xl"><span className="text-secondary text-2xl">{stats.activePositions}</span><span className="text-lg"> / </span><span className="text-2xl">{stats.totalPositions}</span></div><div className="text-sm text-default-500">Total Active LPs</div></div>}<div className="text-center"><div className="text-2xl font-bold">${formatNumber(stats.totalValue, 2)}</div><div className="text-sm text-default-500">Total Value</div></div><div className="text-center"><div className="text-2xl font-bold text-warning">{stats.avgApr.toFixed(1)}%</div><div className="text-sm text-default-500">Avg APR</div></div></div></CardBody></Card>;
    $[63] = isLoading;
    $[64] = isMobile;
    $[65] = positions.length;
    $[66] = refetch;
    $[67] = stats;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== activeFilterCount || $[70] !== chainId || $[71] !== crossChainPositions || $[72] !== currentPage || $[73] !== filter || $[74] !== filteredAndSortedPositions.length || $[75] !== handleIncreaseClick || $[76] !== handleRemoveClick || $[77] !== handleRowClick || $[78] !== handleSendClick || $[79] !== handleSort || $[80] !== isMobile || $[81] !== paginatedPositions || $[82] !== positions.length || $[83] !== refetch || $[84] !== selectedCrossChainIds || $[85] !== selectedPositionId || $[86] !== showPagination || $[87] !== sortDirection || $[88] !== sortKey || $[89] !== totalPages) {
    t21 = positions.length > 0 && <><div className="flex items-center justify-between flex-wrap gap-3 mb-4"><div className="flex items-center gap-2"><Popover placement="bottom-start"><PopoverTrigger><Button variant="flat" size="sm" startContent={<Icon icon="mdi:tune-variant" />} endContent={activeFilterCount > 0 && <Chip size="sm" color="primary" variant="solid" className="h-5 min-w-5">{activeFilterCount}</Chip>}>Options</Button></PopoverTrigger><PopoverContent className="w-72 p-4"><div className="space-y-4"><div><h4 className="text-sm font-semibold mb-2 text-default-700">Sort By</h4><RadioGroup value={sortKey} onValueChange={value => {
                    setSortKey(value as SortKey);
                    setCurrentPage(1);
                  }} color="secondary" className="flex-row gap-x-4" size="sm"><Radio value="value">Value (USD)</Radio><Radio value="apr">APR</Radio><Radio value="pool">Pool Name</Radio></RadioGroup><div className="flex gap-2 mt-4"><Button size="sm" variant={sortDirection === "desc" ? "ghost" : "ghost"} color={sortDirection === "desc" ? "secondary" : "default"} onPress={() => setSortDirection("desc")} startContent={<Icon icon="mdi:sort-descending" />} className="text-shadow-lg/100">High to Low</Button><Button size="sm" variant={sortDirection === "asc" ? "ghost" : "ghost"} color={sortDirection === "asc" ? "secondary" : "default"} onPress={() => setSortDirection("asc")} startContent={<Icon icon="mdi:sort-ascending" />} className="transition-all duration-1">Low to High</Button></div></div><Divider /><div><h4 className="text-sm font-semibold mb-2 text-default-700">Status</h4><RadioGroup value={filter} color="secondary" onValueChange={value_0 => handleFilterChange(value_0 as FilterType)} size="sm"><Radio value="all">All Positions</Radio><Radio value="active">Active Only</Radio><Radio value="v2">V2 Only</Radio><Radio value="v3">V3 Only</Radio></RadioGroup></div>{activeFilterCount > 0 && <><Divider /><Button size="sm" variant="flat" color="danger" className="w-full" onPress={() => {
                    setFilter("all");
                    setSelectedDexes([]);
                    setSortKey("value");
                    setSortDirection("desc");
                    setCurrentPage(1);
                  }} startContent={<Icon icon="mdi:refresh" />}>Reset All Filters</Button></>}</div></PopoverContent></Popover><CrossChainPositionsDropdown currentChainId={chainId || 109} onSelectCrossChainPositions={handleCrossChainSelection} /><Button variant="light" size="sm" isIconOnly={true} onPress={refetch}><Icon icon="mdi:refresh" /></Button></div><div className="flex items-center gap-2 text-sm text-default-500">{selectedCrossChainIds.length > 0 && <Button size="sm" variant="flat" color="warning" onPress={clearCrossChainView} startContent={<Icon icon="mdi:close" />}>Clear cross-chain ({crossChainPositions.length})</Button>}<span>{filteredAndSortedPositions.length} position{filteredAndSortedPositions.length !== 1 ? "s" : ""}</span></div></div><Table aria-label="Liquidity positions" classNames={{
        wrapper: "min-h-[400px] p-0"
      }}><TableHeader><TableColumn onClick={() => handleSort("pool")} className="cursor-pointer">Pool</TableColumn><TableColumn allowsSorting={true} className="cursor-pointer" onClick={() => handleSort("value")}>$ Value</TableColumn><TableColumn className={isMobile ? "hidden" : ""}>In Pair</TableColumn><TableColumn allowsSorting={true} className={`cursor-pointer ${isMobile ? "hidden" : ""}`} onClick={() => handleSort("apr")}>APR</TableColumn></TableHeader><TableBody>{paginatedPositions.map(position_3 => {
            const positionId_0 = `${position_3.contractAddress}-${position_3.token0.address}-${position_3.token1.address}-${position_3.id}`;
            const isSelected = selectedPositionId === positionId_0;
            return <><TableRow key={positionId_0} className={cn("cursor-pointer transition-all duration-200 hover:bg-default-50", isSelected && "bg-primary-50 dark:bg-primary-900/20")} onClick={() => handleRowClick(position_3)}><TableCell><div className="space-y-3"><div className="flex items-center gap-3"><div className="flex items-center -space-x-2"><TokenLogo chainId={chainId} tokenAddress={position_3.token0.address as Address} size="sm" className="border-2 border-background" fallbackText={position_3.token0.symbol?.charAt(0)} /><TokenLogo chainId={chainId} tokenAddress={position_3.token1.address as Address} size="sm" className="border-2 border-background" fallbackText={position_3.token1.symbol?.charAt(0)} /></div><div><div className="font-medium">{position_3.token0.symbol}/{position_3.token1.symbol}</div><div className="text-xs text-default-500 flex items-center gap-1"><img src={DEX_LIST[position_3.dex]?.logo || ""} alt={position_3.dexName} className="w-3 h-3" /><span>{position_3.dexName}</span><Chip size="sm" variant="flat" color="primary">V2</Chip></div></div><div className="ml-auto"><motion.div animate={{
                          rotate: isSelected ? 180 : 0
                        }} transition={{
                          duration: 0.2,
                          ease: "easeInOut"
                        }}><Icon icon="mdi:chevron-down" className="text-lg" /></motion.div></div></div></div></TableCell><TableCell><div className="font-medium">${formatNumber(position_3.totalValueUsd, 2)}</div></TableCell><TableCell className={isMobile ? "hidden" : ""}><div className="text-sm"><div>{formatNumber(parseFloat(position_3.token0AmountFormatted), 4)} {position_3.token0.symbol}</div><div>{formatNumber(parseFloat(position_3.token1AmountFormatted), 4)} {position_3.token1.symbol}</div></div></TableCell><TableCell className={isMobile ? "hidden" : ""}><span className="font-medium">{position_3.apr.toFixed(2)}%</span></TableCell></TableRow>{isSelected && <TableRow key={`${positionId_0}-expanded`}><TableCell colSpan={4} className="p-0"><motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: "auto",
                    opacity: 1
                  }} transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                    opacity: {
                      duration: 0.2
                    }
                  }} style={{
                    willChange: "height, opacity",
                    overflow: "hidden"
                  }}><motion.div initial={{
                      y: -10,
                      opacity: 0
                    }} animate={{
                      y: 0,
                      opacity: 1
                    }} transition={{
                      duration: 0.2,
                      delay: 0.1
                    }} className={cn("px-4 py-3 bg-default-25 dark:bg-default-100/20 border border-divider rounded-lg", "mx-2 mb-2")}>{isMobile && <div className="flex flex-col w-full mb-4"><div className="flex justify-between items-center mb-2"><span className="text-sm text-default-500">APR</span><span className="font-medium">{position_3.apr.toFixed(2)}%</span></div><div className="flex justify-between items-center"><span className="text-sm text-default-500">In Pair</span><div className="text-right text-sm"><div>{formatNumber(parseFloat(position_3.token0AmountFormatted), 4)} {position_3.token0.symbol}</div><div>{formatNumber(parseFloat(position_3.token1AmountFormatted), 4)} {position_3.token1.symbol}</div></div></div></div>}<motion.div className={cn("w-full gap-3", isMobile ? "flex flex-col items-stretch" : "flex flex-wrap justify-center items-center")} initial={{
                        opacity: 0,
                        y: 10
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} transition={{
                        duration: 0.3,
                        delay: 0.2
                      }}><motion.div initial={{
                          opacity: 0,
                          scale: 0.8
                        }} animate={{
                          opacity: 1,
                          scale: 1
                        }} transition={{
                          duration: 0.2,
                          delay: 0.3
                        }} className={isMobile ? "w-full" : ""}><Button color="primary" variant="flat" size={isMobile ? "md" : "sm"} onPress={() => handleIncreaseClick(position_3)} className={isMobile ? "w-full" : ""}>Increase Liquidity</Button></motion.div><motion.div initial={{
                          opacity: 0,
                          scale: 0.8
                        }} animate={{
                          opacity: 1,
                          scale: 1
                        }} transition={{
                          duration: 0.2,
                          delay: 0.4
                        }} className={isMobile ? "w-full" : ""}><Button color="danger" variant="flat" size={isMobile ? "md" : "sm"} onPress={() => handleRemoveClick(position_3)} className={isMobile ? "w-full" : ""}>Remove Liquidity</Button></motion.div><motion.div initial={{
                          opacity: 0,
                          scale: 0.8
                        }} animate={{
                          opacity: 1,
                          scale: 1
                        }} transition={{
                          duration: 0.2,
                          delay: 0.5
                        }} className={isMobile ? "w-full" : ""}><Button variant="bordered" size={isMobile ? "md" : "sm"} onPress={() => handleSendClick(position_3)} className={isMobile ? "w-full" : ""}>Send LP Token</Button></motion.div></motion.div></motion.div></motion.div></TableCell></TableRow>}</>;
          })}</TableBody></Table>{showPagination && <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2"><span className="text-sm text-default-500">Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredAndSortedPositions.length)} of {filteredAndSortedPositions.length}</span><Pagination total={totalPages} page={currentPage} onChange={setCurrentPage} showControls={true} size={isMobile ? "sm" : "md"} classNames={{
          wrapper: "gap-1",
          item: "bg-default-100/50",
          cursor: "bg-primary text-white font-semibold"
        }} /></div>}</>;
    $[69] = activeFilterCount;
    $[70] = chainId;
    $[71] = crossChainPositions;
    $[72] = currentPage;
    $[73] = filter;
    $[74] = filteredAndSortedPositions.length;
    $[75] = handleIncreaseClick;
    $[76] = handleRemoveClick;
    $[77] = handleRowClick;
    $[78] = handleSendClick;
    $[79] = handleSort;
    $[80] = isMobile;
    $[81] = paginatedPositions;
    $[82] = positions.length;
    $[83] = refetch;
    $[84] = selectedCrossChainIds;
    $[85] = selectedPositionId;
    $[86] = showPagination;
    $[87] = sortDirection;
    $[88] = sortKey;
    $[89] = totalPages;
    $[90] = t21;
  } else {
    t21 = $[90];
  }
  let t22;
  if ($[91] !== chainId || $[92] !== isSendModalOpen || $[93] !== onSendModalOpenChange || $[94] !== sendPosition || $[95] !== tokenData) {
    t22 = sendPosition && <SendLPModal position={sendPosition} isOpen={isSendModalOpen} onClose={() => onSendModalOpenChange()} onOpenChange={onSendModalOpenChange} chainId={chainId} tokenData={tokenData} />;
    $[91] = chainId;
    $[92] = isSendModalOpen;
    $[93] = onSendModalOpenChange;
    $[94] = sendPosition;
    $[95] = tokenData;
    $[96] = t22;
  } else {
    t22 = $[96];
  }
  let t23;
  if ($[97] !== className || $[98] !== t19 || $[99] !== t20 || $[100] !== t21 || $[101] !== t22) {
    t23 = <div className={className}>{t19}{t20}{t21}{t22}</div>;
    $[97] = className;
    $[98] = t19;
    $[99] = t20;
    $[100] = t21;
    $[101] = t22;
    $[102] = t23;
  } else {
    t23 = $[102];
  }
  return t23;
}
function _temp9(sum_1, dex_0) {
  return sum_1 + dex_0.foundPositions;
}
function _temp8(dex) {
  return dex.status === "loading-top-pools" || dex.status === "loading-all-pairs";
}
function _temp7(p_4) {
  return p_4.version === "v3";
}
function _temp6(p_5) {
  return p_5.version === "v2";
}
function _temp5(p_6) {
  return !p_6.range?.inRange;
}
function _temp4(p_7) {
  return p_7.range?.inRange;
}
function _temp3(sum_0, p_1) {
  return sum_0 + p_1.apr;
}
function _temp2(p_0) {
  return p_0.range?.inRange;
}
function _temp(sum, p) {
  return sum + (p.totalValueUsd || 0);
}